import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useCurrentOrg } from '../orgs/useCurrentOrg';
import { AgentConfigurationTab } from './AgentConfigurationTab';
import { AgentConsoleHeader } from './AgentConsoleHeader';
import { AgentOverviewTab } from './AgentOverviewTab';
import { AgentRunsTab } from './AgentRunsTab';
import { getAgent, listTasks, listTemplates } from './api';
import { ScheduledTasksSection } from './ScheduledTasksSection';

type Tab = 'overview' | 'runs' | 'triggers' | 'configuration';

const TABS: { value: Tab; label: string }[] = [
  { value: 'overview', label: 'Overview' },
  { value: 'runs', label: 'Runs' },
  { value: 'triggers', label: 'Tasks' },
  { value: 'configuration', label: 'Configuration' },
];

export function AgentDetailPage() {
  const { org } = useCurrentOrg();
  const orgId = org?.id ?? '';
  const { agentID } = useParams<{ agentID: string }>();
  const [tab, setTab] = useState<Tab>('overview');

  const agent = useQuery({
    queryKey: ['agent', orgId, agentID],
    queryFn: () => getAgent(orgId, agentID!),
    enabled: !!orgId && !!agentID,
  });
  const templates = useQuery({
    queryKey: ['agent-templates', orgId],
    queryFn: () => listTemplates(orgId),
    enabled: !!orgId,
  });
  const tasks = useQuery({
    queryKey: ['agent-tasks', orgId, agentID],
    queryFn: () => listTasks(orgId, agentID!),
    enabled: !!orgId && !!agentID,
  });

  const template = useMemo(
    () => templates.data?.find((t) => t.slug === agent.data?.template_slug),
    [templates.data, agent.data?.template_slug],
  );
  const scheduledCount = tasks.data?.filter((t) => t.enabled).length ?? 0;

  if (!agent.data) return <p className="text-sm text-ink-tertiary">Loading…</p>;

  return (
    <div className="space-y-4">
      <AgentConsoleHeader orgId={orgId} agent={agent.data} />

      <div className="flex items-center gap-1 border-b border-line-soft">
        {TABS.map((t) => (
          <TabButton
            key={t.value}
            active={tab === t.value}
            onClick={() => setTab(t.value)}
            badge={t.value === 'triggers' && scheduledCount > 0 ? scheduledCount : undefined}
          >
            {t.label}
          </TabButton>
        ))}
      </div>

      <div className="pt-2">
        {tab === 'overview' && (
          <AgentOverviewTab orgId={orgId} agent={agent.data} template={template} />
        )}
        {tab === 'runs' && <AgentRunsTab orgId={orgId} agentId={agent.data.id} />}
        {tab === 'triggers' && (
          <ScheduledTasksSection orgId={orgId} agentId={agent.data.id} />
        )}
        {tab === 'configuration' && (
          <AgentConfigurationTab orgId={orgId} agent={agent.data} />
        )}
      </div>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  badge,
  children,
}: {
  active: boolean;
  onClick: () => void;
  badge?: number;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative -mb-px flex items-center gap-2 border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
        active
          ? 'border-bit2me text-ink-primary'
          : 'border-transparent text-ink-secondary hover:text-ink-primary'
      }`}
    >
      {children}
      {badge !== undefined && (
        <span className="rounded-full bg-bit2me/10 px-1.5 py-0.5 font-mono text-[10px] text-bit2me">
          {badge}
        </span>
      )}
    </button>
  );
}
